// Vendor Connection v1 — the vendor "Customers" tab: one card per connected
// buyer (multiple loans of the same buyer share a card with per-loan
// sections). The contact card is the server's minimal five-field projection
// — name, business, industry, email, phone — just enough to start the
// contracting conversation. Per loan: the goals carried for that customer,
// the vendor's own report cards (§1/§2 + ratings received), an engagement
// status the vendor drives, private notes the buyer never sees, and the one
// signed-contract slot (replace supersedes; visible to vendor + Phoenix
// only). Expanding a new section stamps it seen (clears the nav badge).

import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, FileText, Mail, Phone, Users } from "lucide-react";
import {
  Badge,
  Button,
  EmptyState,
  SkeletonList,
  StatusBadge,
  SubmitButton,
  SubmittingHint,
  Textarea,
  Timestamp,
  useSubmittingMutation,
  useToast,
  type StatusTone,
} from "@/design-system/components/ui";
import { displayEmail, formatPhone, NO_EMAIL_ON_FILE } from "@/design-system/lib/format";
import {
  ENGAGEMENT_STATUS_LABEL,
  portalVendorCustomersApi,
  type EngagementStatus,
  type VendorCustomerDto,
  type VendorCustomerLoanSectionDto,
} from "../api/connections";
import { formatReportMonth } from "../api/borrower";
import { BorrowerStarsDisplay } from "../components/BorrowerStarRating";
import { useNewConnectionBadge } from "../components/NewConnectionBadgeContext";

const ENGAGEMENT_TONE: Record<EngagementStatus, StatusTone> = {
  "new": "info",
  "contacted": "progress",
  "in-negotiation": "warn",
  "under-contract": "ok",
};

const EDITABLE_STATUSES: EngagementStatus[] = ["new", "contacted", "in-negotiation"];

export function VendorCustomersTab() {
  const [customers, setCustomers] = useState<VendorCustomerDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await portalVendorCustomersApi.list();
        if (!cancelled) setCustomers(rows);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load your customers.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return <p className="text-small text-status-error">{error}</p>;
  }
  if (customers === null) {
    return <SkeletonList data-testid="vendor-customers-loading" />;
  }
  if (customers.length === 0) {
    // Step 5 W1 — this empty state is reached by two very different vendors:
    // one Phoenix has not assigned to anything yet, and one who IS assigned to
    // a loan but whom nobody has connected. A loan party assignment does not
    // create a connection; a separate staff action does. The old copy ("when
    // Phoenix connects you with a customer…") read as "you have no work",
    // which is wrong and un-actionable for the second vendor — they may have
    // been told they are on a deal and see nothing here.
    //
    // So the copy now names the step that is actually outstanding and tells
    // them who moves next, without asking them to chase anything.
    return (
      <EmptyState
        variant="empty"
        icon={Users}
        title="No customers connected yet"
        description="Phoenix connects you to a customer once your assignment is finalised — being assigned to a loan doesn't open the connection on its own. If you've been told you're working a customer and they aren't here, your Phoenix contact can complete the connection."
      />
    );
  }

  return (
    <div className="flex flex-col gap-space-4" data-testid="vendor-customers-list">
      {customers.map((customer) => (
        <article
          key={customer.customerKey}
          className="rounded-lg border border-border bg-white-pure p-space-4"
          data-testid="vendor-customer-card"
        >
          <ContactCard customer={customer} />
          <div className="flex flex-col gap-space-3 mt-space-3">
            {customer.loans.map((loan) => (
              <CustomerLoanSection key={loan.connectionId} initial={loan} />
            ))}
          </div>
        </article>
      ))}
    </div>
  );
}

function ContactCard({ customer }: { customer: VendorCustomerDto }) {
  const { contact } = customer;
  const email = displayEmail(contact.email);
  const hasNew = customer.loans.some((l) => l.isNew);
  return (
    <header data-testid="vendor-customer-contact">
      <div className="flex items-center gap-space-2">
        <h2 className="font-display font-semibold text-h4 m-0">
          {contact.businessName ?? contact.buyerName ?? "Customer"}
        </h2>
        {hasNew && (
          <span
            className="h-2 w-2 rounded-pill bg-gold"
            aria-label="New connection"
            data-testid="vendor-customer-new-dot"
          />
        )}
      </div>
      <dl className="grid grid-cols-2 gap-y-space-1 text-small m-0 mt-space-2">
        {contact.businessName && contact.buyerName && (
          <>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Contact</dt>
            <dd className="m-0">{contact.buyerName}</dd>
          </>
        )}
        {contact.industry && (
          <>
            <dt className="text-caption text-gray-mid uppercase tracking-label">Industry</dt>
            <dd className="m-0">{contact.industry}</dd>
          </>
        )}
        <dt className="text-caption text-gray-mid uppercase tracking-label">Email</dt>
        <dd className="m-0 flex items-center gap-space-1">
          <Mail size={14} strokeWidth={1.5} aria-hidden />
          {email ? (
            <a href={`mailto:${email}`} className="text-gold-shadow hover:underline">
              {email}
            </a>
          ) : (
            <span className="text-gray-mid">{NO_EMAIL_ON_FILE}</span>
          )}
        </dd>
        <dt className="text-caption text-gray-mid uppercase tracking-label">Phone</dt>
        <dd className="m-0 flex items-center gap-space-1">
          <Phone size={14} strokeWidth={1.5} aria-hidden />
          {contact.phone ? formatPhone(contact.phone) : <span className="text-gray-mid">—</span>}
        </dd>
      </dl>
    </header>
  );
}

function CustomerLoanSection({ initial }: { initial: VendorCustomerLoanSectionDto }) {
  const toast = useToast();
  const [loan, setLoan] = useState(initial);
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(!initial.isNew);
  const [notes, setNotes] = useState(initial.notes ?? "");
  const [savedNotes, setSavedNotes] = useState(initial.notes ?? "");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // First view stamps FirstViewedAt and refreshes the sidebar badge — see
  // BorrowerVendorsTab for the rationale (chrome persists across tabs).
  const badge = useNewConnectionBadge();
  const markSeen = useSubmittingMutation(
    () => portalVendorCustomersApi.markViewed(loan.connectionId),
    { refresh: badge.refresh },
  );
  const onToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !seen) {
      setSeen(true);
      void markSeen.submit();
    }
  };

  // Every vendor-side mutation returns the freshly rebuilt section.
  const applyUpdate = (updated: VendorCustomerLoanSectionDto) => setLoan(updated);

  const statusChange = useSubmittingMutation(
    async (status: EngagementStatus) => {
      applyUpdate(await portalVendorCustomersApi.updateEngagementStatus(loan.connectionId, status));
      toast.success("Engagement status updated.");
    },
    {
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : "Couldn't update the status."),
    },
  );

  const saveNotes = useSubmittingMutation(
    async () => {
      const updated = await portalVendorCustomersApi.updateNotes(
        loan.connectionId,
        notes.trim() === "" ? null : notes,
      );
      applyUpdate(updated);
      setSavedNotes(updated.notes ?? "");
      setNotes(updated.notes ?? "");
      toast.success("Notes saved.");
    },
    {
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : "Couldn't save your notes."),
    },
  );
  const saving = saveNotes.pending;

  const uploadContract = useSubmittingMutation(
    async (file: File) => {
      try {
        applyUpdate(await portalVendorCustomersApi.uploadContract(loan.connectionId, file));
        toast.success("Contract uploaded — Phoenix can see it too.");
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    {
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : "Couldn't upload the contract."),
    },
  );
  const uploading = uploadContract.pending;

  const onDownloadContract = async () => {
    try {
      const download = await portalVendorCustomersApi.contractDownloadUrl(loan.connectionId);
      window.open(download.sasUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't open the contract.");
    }
  };

  return (
    <section className="rounded-md border border-border" data-testid="vendor-customer-loan">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center gap-space-2 p-space-3 text-left"
        data-testid="vendor-customer-loan-toggle"
      >
        {open ? (
          <ChevronDown size={16} strokeWidth={1.5} aria-hidden />
        ) : (
          <ChevronRight size={16} strokeWidth={1.5} aria-hidden />
        )}
        <span className="font-display font-semibold text-small">{loan.loanLabel}</span>
        <span className="text-caption text-gray-mid">{loan.roleOnLoan}</span>
        <StatusBadge tone={ENGAGEMENT_TONE[loan.engagementStatus]}>
          {ENGAGEMENT_STATUS_LABEL[loan.engagementStatus]}
        </StatusBadge>
        {!seen && (
          <span className="h-2 w-2 rounded-pill bg-gold" aria-label="New" />
        )}
        <span className="ml-auto text-caption text-gray-mid">
          Connected <Timestamp value={loan.connectedAt} />
        </span>
      </button>

      {open && (
        <div className="px-space-3 pb-space-3 flex flex-col gap-space-3">
          <div data-testid="vendor-customer-engagement">
            <h3 className="font-display font-semibold text-small m-0 mb-space-1">
              Engagement
            </h3>
            {loan.engagementStatus === "under-contract" ? (
              <p className="text-small text-gray-mid m-0">
                Under contract — uploaded agreements set this automatically.
              </p>
            ) : (
              <div className="flex items-center gap-space-2 flex-wrap">
                {EDITABLE_STATUSES.map((status) => (
                  <Button
                    key={status}
                    variant={status === loan.engagementStatus ? "primary" : "secondary"}
                    onClick={() => void statusChange.submit(status)}
                    disabled={statusChange.pending}
                    data-testid={`vendor-customer-status-${status}`}
                  >
                    {ENGAGEMENT_STATUS_LABEL[status]}
                  </Button>
                ))}
                <SubmittingHint
                  pending={statusChange.pending}
                  message="Updating engagement status…"
                  className="w-full"
                />
              </div>
            )}
          </div>

          <div data-testid="vendor-customer-contract">
            <h3 className="font-display font-semibold text-small m-0 mb-space-1">
              Signed contract
            </h3>
            <p className="text-caption text-gray-mid m-0 mb-space-1">
              Visible to you and Phoenix only. Uploading a new version replaces the
              current one (the history is kept).
            </p>
            {loan.contract && (
              <p className="text-small m-0 mb-space-1 flex items-center gap-space-1">
                <FileText size={14} strokeWidth={1.5} aria-hidden />
                <button
                  type="button"
                  onClick={() => void onDownloadContract()}
                  className="text-gold-shadow hover:underline"
                  data-testid="vendor-customer-contract-download"
                >
                  {loan.contract.fileName}
                </button>
                <span className="text-caption text-gray-mid">
                  v{loan.contract.version} · <Timestamp value={loan.contract.uploadedAt} />
                </span>
              </p>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              data-testid="vendor-customer-contract-input"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void uploadContract.submit(file);
              }}
            />
            <SubmitButton
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              pending={uploading}
              pendingLabel="Uploading…"
              data-testid="vendor-customer-contract-upload"
            >
              {loan.contract ? "Replace contract" : "Upload signed contract"}
            </SubmitButton>
            <SubmittingHint pending={uploading} className="mt-space-1" />
          </div>

          <div>
            <h3 className="font-display font-semibold text-small m-0 mb-space-1">
              Goals you carry for this customer
            </h3>
            {loan.goals.length === 0 ? (
              <p className="text-small text-gray-mid m-0">No goals assigned yet.</p>
            ) : (
              <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
                {loan.goals.map((goal) => (
                  <li key={goal.goalId} className="text-small">
                    <span className="flex items-center gap-space-2">
                      <span>{goal.title}</span>
                      <Badge>{goal.status}</Badge>
                      {goal.targetMonths && (
                        <span className="text-caption text-gray-mid">
                          months {goal.targetMonths}
                        </span>
                      )}
                    </span>
                    {goal.completionCriteria && (
                      <span className="block text-caption text-gray-mid">
                        Done when: {goal.completionCriteria}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h3 className="font-display font-semibold text-small m-0 mb-space-1">
              Your report cards
            </h3>
            {loan.reports.length === 0 ? (
              <p className="text-small text-gray-mid m-0">No accepted reports yet.</p>
            ) : (
              <ul className="m-0 p-0 list-none flex flex-col gap-space-2">
                {loan.reports.map((report) => (
                  <li
                    key={report.reportId}
                    className="rounded-md bg-white-warm p-space-2"
                    data-testid="vendor-customer-report-card"
                  >
                    <div className="flex items-center gap-space-2">
                      <span className="font-display font-semibold text-small">
                        {formatReportMonth(report.periodStart)}
                      </span>
                      {report.signOff ? (
                        <BorrowerStarsDisplay value={report.signOff.overallRating} />
                      ) : (
                        <Badge>Awaiting customer sign-off</Badge>
                      )}
                    </div>
                    {report.goalGrades.length > 0 && (
                      <ul className="m-0 mt-space-1 p-0 list-none flex flex-col gap-[2px]">
                        {report.goalGrades.map((grade) => (
                          <li key={grade.goalId} className="text-caption text-gray-mid">
                            {grade.goalTitle}: {grade.score}/100
                            {grade.isComplete ? " · complete" : ""}
                          </li>
                        ))}
                      </ul>
                    )}
                    {report.signOff?.feedback && (
                      <p className="text-caption text-gray-mid m-0 mt-space-1 italic">
                        “{report.signOff.feedback}”
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h3 className="font-display font-semibold text-small m-0 mb-space-1">
              My notes
            </h3>
            <p className="text-caption text-gray-mid m-0 mb-space-1">
              Private to you — the customer never sees these.
            </p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              data-testid="vendor-customer-notes"
            />
            <div className="mt-space-1 flex flex-col items-start gap-space-1">
              <SubmitButton
                variant="secondary"
                onClick={() => void saveNotes.submit()}
                pending={saving}
                pendingLabel="Saving…"
                disabled={notes === savedNotes}
                data-testid="vendor-customer-notes-save"
              >
                Save notes
              </SubmitButton>
              <SubmittingHint pending={saving} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
